/**
 * Resolve a cost type's calculated value from an instrument's flattened metadata.
 *
 * A cost type stores `apiKeys` as a JSON-stringified string[]. Keys are tried in
 * order — exact match first, then case-insensitive — and the first meaningful
 * numeric value wins. Malformed `apiKeys` (invalid JSON, not an array) and
 * non-string entries are ignored.
 *
 * Shared by the product dialog in apps/web and the cost service in apps/api so
 * the two can never drift.
 */

export type CostApiValue = { value: number; sourceKey: string };

function toNumber(val: unknown): number {
  return typeof val === "number" ? val : parseFloat(val as string);
}

function isMeaningfulValue(val: unknown): boolean {
  if (val === undefined || val === null) return false;
  const num = toNumber(val);
  return !Number.isNaN(num) && Math.abs(num) >= 0.0001;
}

/** Parse a cost type's JSON-stringified `apiKeys`, dropping anything that isn't a string. */
export function parseCostApiKeys(apiKeys: string): string[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(apiKeys);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.filter((k): k is string => typeof k === "string");
}

export function pickCostApiValue(
  metadata: Record<string, unknown>,
  apiKeys: string,
): CostApiValue | null {
  for (const key of parseCostApiKeys(apiKeys)) {
    if (isMeaningfulValue(metadata[key])) {
      return { value: toNumber(metadata[key]), sourceKey: key };
    }
    const lowerKey = key.toLowerCase();
    const matched = Object.keys(metadata).find((k) => k.toLowerCase() === lowerKey);
    if (matched && isMeaningfulValue(metadata[matched])) {
      return { value: toNumber(metadata[matched]), sourceKey: key };
    }
  }
  return null;
}
